import { Capacitor } from '@capacitor/core';
import type { OidcResult } from './protocol';
import { apiBase } from './apiBase';

/**
 * Drives a social sign-in.
 *
 * Two very different mechanisms depending on where we're running, because a
 * native WebView doesn't give a `window.open()` popup a same-process opener
 * the way a desktop/mobile browser tab does:
 *
 *  - Web: opens a POPUP rather than navigating the page (navigating away would
 *    tear down the game, its WebSocket, room seat and in-progress match, just
 *    to log in). The popup posts its result back via `postMessage` and closes.
 *
 *  - Native (Capacitor): opens the provider flow in the OS's in-app browser
 *    (`@capacitor/browser`) and waits for the server's callback to redirect to
 *    this app's custom URL scheme (`<appId>://auth-callback?...`, registered
 *    in the native project — see android/.../AndroidManifest.xml and
 *    ios/.../Info.plist). The OS hands that URL back to us as an
 *    `appUrlOpen` event; see server/authroutes.mjs's `native` branch.
 *
 * Nothing here trusts the callback's word for who you are — it only carries a
 * session token the server already minted after verifying the provider's
 * id_token.
 */
export function signInWith(provider: string, currentToken?: string | null): Promise<OidcResult> {
  return Capacitor.isNativePlatform()
    ? signInNative(provider, currentToken)
    : signInWeb(provider, currentToken);
}

function authUrl(provider: string, currentToken: string | null | undefined, native: boolean): string {
  const params = new URLSearchParams();
  if (currentToken) params.set('token', currentToken);
  if (native) params.set('app', '1');
  const qs = params.toString();
  return `${apiBase()}/auth/${encodeURIComponent(provider)}${qs ? `?${qs}` : ''}`;
}

function signInWeb(provider: string, currentToken?: string | null): Promise<OidcResult> {
  return new Promise((resolve) => {
    const url = authUrl(provider, currentToken, false);
    const w = 480;
    const h = 640;
    // centre on the CURRENT screen, which is not always the primary one
    const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
    const popup = window.open(url, 'carom-oidc', `width=${w},height=${h},left=${left},top=${top}`);

    if (!popup) {
      resolve({ error: 'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.' });
      return;
    }

    let done = false;
    const finish = (result: OidcResult): void => {
      if (done) return;
      done = true;
      window.removeEventListener('message', onMessage);
      clearInterval(poll);
      resolve(result);
    };

    const onMessage = (e: MessageEvent): void => {
      // Only the server's own origin may speak for a sign-in — without this
      // check any page could post a forged token into the app.
      if (e.origin !== new URL(apiBase()).origin) return;
      const data = e.data as { source?: string; result?: OidcResult } | null;
      if (!data || data.source !== 'carom-oidc') return;
      finish(data.result ?? { error: 'Empty sign-in result' });
    };
    window.addEventListener('message', onMessage);

    // The popup closing without a message means the user dismissed it. There is
    // no event for that, so it has to be polled.
    const poll = window.setInterval(() => {
      if (popup.closed) finish({ error: '' }); // empty message = cancelled, not failed
    }, 400);
  });
}

async function signInNative(provider: string, currentToken?: string | null): Promise<OidcResult> {
  // Loaded lazily: these packages only matter on native, and pulling them into
  // the web bundle unconditionally would grow it for nothing.
  const [{ Browser }, { App: CapApp }] = await Promise.all([
    import('@capacitor/browser'),
    import('@capacitor/app'),
  ]);

  return new Promise((resolve) => {
    let done = false;
    const finish = async (result: OidcResult): Promise<void> => {
      if (done) return;
      done = true;
      void urlSub.then((h) => h.remove());
      void closeSub.then((h) => h.remove());
      await Browser.close().catch(() => {});
      resolve(result);
    };

    const urlSub = CapApp.addListener('appUrlOpen', ({ url }) => {
      let parsed: URL;
      try {
        parsed = new URL(url);
      } catch {
        return;
      }
      if (parsed.pathname.replace(/^\/*/, '') !== 'auth-callback' && parsed.host !== 'auth-callback') return;
      const q = parsed.searchParams;
      const error = q.get('error');
      void finish(
        error
          ? { error }
          : {
              token: q.get('token') ?? undefined,
              id: q.get('id') ?? undefined,
              name: q.get('name') ?? undefined,
              provider: q.get('provider') ?? undefined,
              linked: (q.get('linked') as OidcResult['linked']) ?? undefined,
            },
      );
    });

    // The user closing the in-app browser without completing sign-in (the
    // provider flow never redirects) means "cancelled", not failed.
    const closeSub = Browser.addListener('browserFinished', () => void finish({ error: '' }));

    Browser.open({ url: authUrl(provider, currentToken, true) }).catch((e: unknown) => {
      void finish({ error: e instanceof Error ? e.message : 'Could not open sign-in' });
    });
  });
}

/**
 * A token can also arrive as `#token=…` when the popup had no opener (someone
 * opened the URL in a normal tab). Returns it and scrubs the fragment so it
 * can't be re-read from history or leak into a shared link.
 */
export function takeTokenFromFragment(): string | null {
  const m = /(?:^|[#&])token=([^&]+)/.exec(window.location.hash);
  if (!m) return null;
  history.replaceState(null, '', window.location.pathname + window.location.search);
  return decodeURIComponent(m[1]);
}
